import { useLayoutEffect, useMemo, useRef, useState } from "react";
import type { Initiative, InitiativeLink, Team } from "@/lib/types";
import { positionInQuarter, quarterRange, fmtDate } from "@/lib/quarter";
import { cn } from "@/lib/utils";

interface Props {
  initiatives: Initiative[];
  links: InitiativeLink[];
  teams: Team[];
  quarter: string;
  onSelect: (i: Initiative) => void;
}

const ROW_HEIGHT = 44;
const ROW_GAP = 8;

interface BarRect {
  id: number;
  left: number;
  top: number;
  width: number;
  height: number;
}

export function RoadmapView({ initiatives, links, teams, quarter, onSelect }: Props) {
  const qr = useMemo(() => quarterRange(quarter), [quarter]);

  const grouped = useMemo(() => {
    const byTeam = new Map<number, Initiative[]>();
    for (const i of initiatives) {
      if (!byTeam.has(i.team_id)) byTeam.set(i.team_id, []);
      byTeam.get(i.team_id)!.push(i);
    }
    const lanes = new Map<number, Initiative[][]>();
    for (const team of teams) {
      const items = byTeam.get(team.id);
      if (!items) continue;
      const sorted = [...items].sort((a, b) => a.start_date.localeCompare(b.start_date));
      const teamLanes: Initiative[][] = [];
      for (const it of sorted) {
        let placed = false;
        for (const lane of teamLanes) {
          const last = lane[lane.length - 1];
          if (last.end_date < it.start_date) {
            lane.push(it);
            placed = true;
            break;
          }
        }
        if (!placed) teamLanes.push([it]);
      }
      lanes.set(team.id, teamLanes);
    }
    return teams
      .filter((t) => lanes.has(t.id))
      .map((team) => ({ team, lanes: lanes.get(team.id)! }));
  }, [initiatives, teams]);

  // Track each bar's DOM rect to draw SVG arrows over it.
  const trackRef = useRef<HTMLDivElement>(null);
  const barRefs = useRef<Map<number, HTMLButtonElement>>(new Map());
  const [rects, setRects] = useState<Map<number, BarRect>>(new Map());

  useLayoutEffect(() => {
    function measure() {
      const track = trackRef.current;
      if (!track) return;
      const trackBox = track.getBoundingClientRect();
      const next = new Map<number, BarRect>();
      for (const [id, el] of barRefs.current) {
        if (!el) continue;
        const r = el.getBoundingClientRect();
        next.set(id, {
          id,
          left: r.left - trackBox.left,
          top: r.top - trackBox.top,
          width: r.width,
          height: r.height,
        });
      }
      setRects(next);
    }
    measure();
    const ro = new ResizeObserver(measure);
    if (trackRef.current) ro.observe(trackRef.current);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [grouped, quarter]);

  const visibleIds = useMemo(() => new Set(initiatives.map((i) => i.id)), [initiatives]);
  const visibleLinks = useMemo(
    () =>
      links.filter(
        (l) => visibleIds.has(l.from_id) && visibleIds.has(l.to_id) && rects.has(l.from_id) && rects.has(l.to_id),
      ),
    [links, visibleIds, rects],
  );

  return (
    <div className="rounded-xl border bg-card overflow-hidden">
      {/* Month header */}
      <div className="flex border-b bg-muted/40">
        <div className="w-44 shrink-0 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground border-r">
          Team
        </div>
        <div className="flex-1 grid grid-cols-3 text-center">
          {qr.months.map((m) => (
            <div
              key={m.name}
              className="py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground border-r last:border-r-0"
            >
              {m.name}
            </div>
          ))}
        </div>
      </div>

      {grouped.length === 0 ? (
        <div className="px-4 py-12 text-center text-sm text-muted-foreground">
          No initiatives match the current filters.
        </div>
      ) : (
        <div className="flex">
          {/* Team labels column */}
          <div className="w-44 shrink-0 border-r">
            {grouped.map(({ team, lanes }) => {
              const height = lanes.length * ROW_HEIGHT + (lanes.length - 1) * ROW_GAP + 16;
              return (
                <div
                  key={team.id}
                  className="px-4 py-3 border-b last:border-b-0 flex items-start gap-2"
                  style={{ height }}
                >
                  <span
                    className="mt-1 h-2.5 w-2.5 rounded-sm shrink-0"
                    style={{ backgroundColor: team.color }}
                  />
                  <div>
                    <div className="text-sm font-medium">{team.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {lanes.flat().length} initiative{lanes.flat().length === 1 ? "" : "s"}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Track area + arrows overlay */}
          <div ref={trackRef} className="relative flex-1">
            {grouped.map(({ team, lanes }) => {
              const height = lanes.length * ROW_HEIGHT + (lanes.length - 1) * ROW_GAP + 16;
              return (
                <div
                  key={team.id}
                  className="relative swimlane-track border-b last:border-b-0"
                  style={{ height }}
                >
                  {lanes.map((lane, laneIdx) =>
                    lane.map((it) => (
                      <RoadmapBar
                        key={it.id}
                        qrKey={qr.key}
                        initiative={it}
                        teamColor={team.color}
                        top={8 + laneIdx * (ROW_HEIGHT + ROW_GAP)}
                        innerRef={(el) => {
                          if (el) barRefs.current.set(it.id, el);
                          else barRefs.current.delete(it.id);
                        }}
                        onClick={() => onSelect(it)}
                      />
                    )),
                  )}
                </div>
              );
            })}

            {/* SVG dependency arrows */}
            <ArrowsOverlay links={visibleLinks} rects={rects} />
          </div>
        </div>
      )}
    </div>
  );
}

function ArrowsOverlay({
  links,
  rects,
}: {
  links: InitiativeLink[];
  rects: Map<number, BarRect>;
}) {
  if (links.length === 0) return null;
  return (
    <svg
      className="pointer-events-none absolute inset-0 z-[5]"
      width="100%"
      height="100%"
      style={{ overflow: "visible" }}
    >
      <defs>
        <marker
          id="arrow-blocks"
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth="6"
          markerHeight="6"
          orient="auto-start-reverse"
        >
          <path d="M0,0 L10,5 L0,10 Z" fill="hsl(var(--priority-p0))" opacity="0.85" />
        </marker>
        <marker
          id="arrow-related"
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth="5"
          markerHeight="5"
          orient="auto-start-reverse"
        >
          <path d="M0,0 L10,5 L0,10 Z" fill="hsl(var(--muted-foreground))" opacity="0.6" />
        </marker>
      </defs>
      {links.map((l) => {
        const a = rects.get(l.from_id)!;
        const b = rects.get(l.to_id)!;
        const x1 = a.left + a.width;
        const y1 = a.top + a.height / 2;
        const x2 = b.left;
        const y2 = b.top + b.height / 2;
        const dx = Math.max(40, (x2 - x1) / 2);
        const path = `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;
        const isBlock = l.type === "blocks";
        return (
          <path
            key={l.id}
            d={path}
            fill="none"
            stroke={isBlock ? "hsl(var(--priority-p0))" : "hsl(var(--muted-foreground))"}
            strokeOpacity={isBlock ? 0.7 : 0.4}
            strokeWidth={isBlock ? 1.6 : 1.2}
            strokeDasharray={isBlock ? undefined : "4 3"}
            markerEnd={`url(#${isBlock ? "arrow-blocks" : "arrow-related"})`}
          />
        );
      })}
    </svg>
  );
}

function RoadmapBar({
  qrKey,
  initiative,
  teamColor,
  top,
  innerRef,
  onClick,
}: {
  qrKey: string;
  initiative: Initiative;
  teamColor: string;
  top: number;
  innerRef: (el: HTMLButtonElement | null) => void;
  onClick: () => void;
}) {
  const qr = quarterRange(qrKey);
  const { leftPct, widthPct } = positionInQuarter(qr, initiative.start_date, initiative.end_date);
  if (widthPct <= 0) return null;

  const dim = initiative.status === "done" || initiative.status === "cancelled";
  const hatched = initiative.status === "cancelled";

  return (
    <button
      ref={innerRef}
      onClick={onClick}
      className={cn(
        "absolute rounded-md px-2.5 py-1.5 text-left transition-all",
        "hover:scale-[1.02] hover:shadow-lg hover:z-10",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        "border backdrop-blur-sm overflow-hidden",
        dim && "opacity-60",
      )}
      style={{
        left: `${leftPct}%`,
        width: `calc(${widthPct}% - 4px)`,
        top: `${top}px`,
        height: `${ROW_HEIGHT}px`,
        backgroundColor: `${teamColor}28`,
        borderColor: `${teamColor}88`,
        backgroundImage: hatched
          ? `repeating-linear-gradient(45deg, transparent, transparent 6px, ${teamColor}30 6px, ${teamColor}30 12px)`
          : undefined,
      }}
      title={`${initiative.title} — ${fmtDate(initiative.start_date)} → ${fmtDate(initiative.end_date)}`}
    >
      <div className="flex items-center gap-1.5">
        <PriorityDot priority={initiative.priority} />
        <span className="text-xs font-semibold truncate">{initiative.title}</span>
      </div>
      <div className="text-[10px] text-muted-foreground truncate">
        {initiative.product_name}
        {initiative.owner ? ` · ${initiative.owner}` : ""}
      </div>
    </button>
  );
}

function PriorityDot({ priority }: { priority: Initiative["priority"] }) {
  const cls =
    priority === "P0"
      ? "bg-priority-p0"
      : priority === "P1"
      ? "bg-priority-p1"
      : "bg-priority-p2";
  return <span className={cn("h-1.5 w-1.5 rounded-full shrink-0", cls)} />;
}
